import { browserHandoffStore, createBrowserAnalytics, type BrowserAnalyticsClient } from '@highlight/analytics/browser'
import { isRenderer } from '@highlight/ipc'
import GlobalStateUtils from '@shared/ipc/GlobalStateUtils.ts'
import {
  amplitudeJoinKey,
  analyticsEnvironment,
  currentUserId,
  identityTransition,
} from '@shared/utils/posthog-identity.ts'
import { getInstalledVersionWithSuffix, getStartupChannel } from '@shared/utils/builds/channels.shared'
import * as Sentry from '@sentry/electron/renderer'

/**
 * PostHog for the renderers.
 *
 * Runs alongside `AmplitudeRenderer` while events migrate, on PostHog's own
 * ids rather than Amplitude's. See `analytics.main.ts`.
 */

if (!isRenderer) {
  throw new Error('Cannot import AnalyticsRenderer in non-renderer processes')
}

export const AnalyticsRenderer: BrowserAnalyticsClient = createBrowserAnalytics({
  surface: 'desktop-renderer',
  environment: analyticsEnvironment(),
  onEvent: (event) => console.info(`[AnalyticsRenderer] would capture ${event.name}`, event.properties),
  // Every process reports as one person. Read from the launch argument, not
  // from the store: the renderer's store is still syncing at module load, and
  // posthog-js mints and persists its own id the moment it starts without one,
  // then ignores any later bootstrap. One user would arrive as several.
  bootstrapDistinctId: window.__POSTHOG_DEVICE_ID,
  configOverrides: {
    // Renderers load over a custom protocol, where cookies are not writable.
    persistence: 'localStorage',
  },
  // Without this the renderer is the one surface where the two streams cannot
  // be compared. posthog-js mints its own anonymous id, Amplitude keys on the
  // device id, and nothing relates them, so signed-out desktop traffic is two
  // unconnected people. Landing and the main process both avoid it by already
  // reporting under the Amplitude id. Drop it once Amplitude is retired.
  getUserId: currentUserId,
  baseProperties: () => ({
    isDeveloper: process.env.NODE_ENV === 'development',
    platform: 'desktop',
    deviceFamily: process.platform === 'win32' ? 'Windows' : 'Mac',
    version: getInstalledVersionWithSuffix(),
    channel: getStartupChannel(),
    rendererName: window.__RENDERER_NAME,
    amplitude_device_id: amplitudeJoinKey(),
  }),
  onError: (error, context) => {
    console.error(`[AnalyticsRenderer] ${context.sink}/${context.operation} failed`, error)
  },
})

/**
 * Who PostHog was last identified as, kept where it outlives the process.
 *
 * posthog-js persists its identity in localStorage, so it survives a restart
 * while a module variable does not. Remembering this in memory left the two
 * out of sync on the next launch: a launch that starts signed out matched
 * `undefined` against `undefined`, skipped the reset, and went on attributing
 * anonymous events to the account from the previous session.
 */
const identifiedUser = browserHandoffStore('highlight_posthog_identified_user_id')

/**
 * @param observedChange whether the user id actually changed while running, as
 * opposed to this being the first read at startup. An absent user means
 * "signed out" only in the first case: at startup it is indistinguishable from
 * auth not having hydrated yet, and `GlobalState` has no signal that separates
 * them. Treating startup as a sign-out fired a signed-out event and reset
 * PostHog on every signed-in launch, before the real user arrived.
 */
const syncCurrentUser = (observedChange: boolean) => {
  try {
    const userId = currentUserId()
    const identifiedUserId = identifiedUser.read()
    const transition = identityTransition({ userId, identifiedUserId, observedChange })
    // A signed-in user is identified again at startup rather than trusted to
    // the stored marker, which can claim an identify posthog-js never applied.
    // posthog-js sends nothing when the id already matches.
    if (transition === 'none' && !userId) {
      return
    }

    // Why a sign-out resets, and why an absent user at startup does not count
    // as one, both live in `identityTransition`.
    if (identifiedUserId && (transition === 'signOut' || transition === 'switch')) {
      // Named explicitly, not left to the ambient identity. By the time this
      // runs auth has already moved on, so the ambient read is the incoming
      // account on a switch and nobody at all on a clean sign-out. Either way
      // the event would describe the wrong person leaving.
      AnalyticsRenderer.signOut({ distinctId: identifiedUserId, userId: identifiedUserId })
      identifiedUser.write(undefined)
    }

    if (!userId) {
      return
    }

    // No alias. The device id is per machine and outlives any one account, so
    // aliasing it onto a sign-in merges whoever signed in here before into
    // whoever signs in now, irreversibly. `identify` sends posthog-js's own
    // anonymous id as `$anon_distinct_id`, which merges only the signed-out
    // session.
    AnalyticsRenderer.identify({ distinctId: userId, userId })
    identifiedUser.write(userId)
  } catch (error) {
    Sentry.captureException(error)
  }
}

let initialized = false

export const initializeAnalytics = () => {
  // Idempotent: a caller that re-runs must not stack another user listener.
  if (initialized) {
    return
  }
  initialized = true

  syncCurrentUser(false)
  GlobalStateUtils.user.onUserIdChange(() => syncCurrentUser(true))
}
